import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Check,
  X,
  Search,
  MessageSquare,
  Clock,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import apiEndpoints from "../../apiconfig";

export default function Enquiries() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiEndpoints.enquiries}?action=list`);
      const data = await response.json();
      if (data.status === "success") {
        setEnquiries(data.data || []);
      } else {
        showSnackbar(data.message || "Failed to fetch enquiries", "error");
      }
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      showSnackbar("Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const handleUpdateStatus = async (id, status) => {
    if (status === "rejected") {
      setDeleteDialog({ open: true, id });
      return;
    }

    performUpdate(id, status);
  };

  const performUpdate = async (id, status) => {
    try {
      const response = await fetch(apiEndpoints.enquiries, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_status", id, status }),
      });
      const data = await response.json();
      if (data.status === "success") {
        showSnackbar(
          `Enquiry ${status === "rejected" ? "removed" : status} successfully`,
          "success",
        );

        if (status === "rejected") {
          setEnquiries((prev) => prev.filter((e) => e.id !== id));
          setDeleteDialog({ open: false, id: null });
        } else {
          setEnquiries((prev) =>
            prev.map((e) => (e.id === id ? { ...e, status } : e)),
          );
        }
      } else {
        showSnackbar(data.message || "Failed to process request", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar("Server error. Please try again.", "error");
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const filteredEnquiries = enquiries.filter(
    (e) =>
      e.requester_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.receiver_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.requester_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.receiver_code.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Box
      sx={{ width: "100%", pb: 4 }}
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Enquiries Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage interest requests between users
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, borderRadius: "12px" }}>
        <TextField
          fullWidth
          placeholder="Search by name or user ID..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "12px", overflow: "hidden" }}>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ bgcolor: "rgba(0,0,0,0.03)" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Requester</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Receiver</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Plan Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress color="secondary" />
                  </TableCell>
                </TableRow>
              ) : filteredEnquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      No enquiries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEnquiries.map((enquiry) => (
                  <TableRow key={enquiry.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          src={`${apiEndpoints.image}${enquiry.requester_photo}`}
                          sx={{
                            width: 40,
                            height: 40,
                            border: "1px solid #eee",
                          }}
                        />
                        <Box>
                          <Typography fontWeight={600} variant="body2">
                            {enquiry.requester_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {enquiry.requester_code}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          src={`${apiEndpoints.image}${enquiry.receiver_photo}`}
                          sx={{
                            width: 40,
                            height: 40,
                            border: "1px solid #eee",
                          }}
                        />
                        <Box>
                          <Typography fontWeight={600} variant="body2">
                            {enquiry.receiver_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {enquiry.receiver_code}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={enquiry.plan_type_at_request || "Standard"}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        color="text.secondary"
                      >
                        <Clock size={14} />
                        <Typography variant="body2">
                          {new Date(enquiry.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={enquiry.status.toUpperCase()}
                        color={getStatusColor(enquiry.status)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() =>
                              handleUpdateStatus(enquiry.id, "accepted")
                            }
                            disabled={enquiry.status === "accepted"}
                            sx={{
                              bgcolor: "rgba(46, 125, 50, 0.08)",
                              "&:hover": { bgcolor: "rgba(46, 125, 50, 0.15)" },
                            }}
                          >
                            <Check size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleUpdateStatus(enquiry.id, "rejected")
                            }
                            disabled={enquiry.status === "rejected"}
                            sx={{
                              bgcolor: "rgba(211, 47, 47, 0.08)",
                              "&:hover": { bgcolor: "rgba(211, 47, 47, 0.15)" },
                            }}
                          >
                            <X size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: "8px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Custom Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        PaperProps={{
          sx: { borderRadius: "16px", p: 1, maxWidth: "400px" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "error.main",
            fontWeight: 700,
          }}
        >
          <AlertTriangle size={24} />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ mb: 2, fontWeight: 500, color: "text.primary" }}
          >
            Are you sure you want to permanently remove this enquiry?
          </DialogContentText>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone and the record will be erased from the
            database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, id: null })}
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => performUpdate(deleteDialog.id, "rejected")}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "none",
              backgroundColor: theme.palette.error.main,
              "&:hover": {
                backgroundColor: theme.palette.error.main,
                boxShadow: "none",
              },
            }}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
